import type React from 'react'
import type { JSX } from 'react'

import { useRef } from 'react'
import { TextBlockProgramCard } from 'blocks/TextBlock/Templates'
import { useHover } from 'utils/hooks'
import { COLOR, CORNERS } from 'vars/index'
import {
  getIsRenderTitle,
  getProgramDescription,
  getProgramTitle,
  isRenderCard,
  MAX_THREE_DOTS_WIDTH,
  MIN_THREE_DOTS_WIDTH,
  MINUTE_MS,
  SMALL_PROGRAM_MIN,
} from './lib'
import { ProgramCardSC } from './ProgramCard.styled'

export interface IProgramCard {
  background?: COLOR
  width?: number
  title?: string
  description?: string
  descriptionHover?: string
  selected?: boolean
  disabled?: boolean
  colorDisabled?: COLOR
  backgroundHovered?: COLOR
  onClick?: (e: React.MouseEvent) => void
  height?: number
  step: number
  startTimeLine: number
  stopTimeLine: number
  startMs: number
  stopMs: number
  // duration: number // TODO could be used
  isLive?: boolean
  clickableCard?: boolean
  locked?: boolean
  lockedTitle?: string
  fallbackTitle?: string
  corners?: CORNERS
  tableGap?: number
  sectionWidth?: number
}

export const ProgramCard = ({
  background = COLOR.BACKGROUND_PROGRAM_CARD,
  backgroundHovered = COLOR.SURFACE_OPACITY_DARK_70,
  title: programTitle = '',
  description: programDescription = '',
  descriptionHover: programDescriptionHover = '',
  selected,
  disabled,
  colorDisabled = COLOR.TEXT_ICON_TERTIARY,
  onClick,
  height = 84,
  step = 0,
  startTimeLine = 0,
  stopTimeLine = 0,
  startMs = 0,
  stopMs = 0,
  isLive,
  clickableCard,
  locked,
  lockedTitle,
  fallbackTitle,
  corners = CORNERS.CORNERS_0,
  tableGap = 2,
}: IProgramCard): JSX.Element | null => {
  const hoverRef = useRef<HTMLDivElement>(null)
  const isHovered = useHover(hoverRef)

  const programDurationMin = (stopMs - startMs) / MINUTE_MS

  const programWidthInPixels = programDurationMin * step
  const minProgramPixels = SMALL_PROGRAM_MIN * step
  const isThreeDots =
    programWidthInPixels >= MIN_THREE_DOTS_WIDTH &&
    programWidthInPixels <= MAX_THREE_DOTS_WIDTH

  const isRender = isRenderCard({
    programWidthInPixels,
    step,
    startTimeLine,
    stopTimeLine,
    startMs,
    stopMs,
  })

  if (!isRender) return null

  const title = getProgramTitle({
    locked,
    lockedTitle,
    programTitle,
    fallbackTitle,
    programWidthInPixels,
  })

  const description = getProgramDescription({
    locked,
    title: programTitle,
    isHovered,
    disabled,
    description: programDescription,
    descriptionHover: programDescriptionHover,
    isThreeDots,
  })

  const isRenderTitle = getIsRenderTitle({
    title,
    locked,
    programWidthInPixels,
    minProgramPixels,
  })

  const isDisabled = disabled || locked || !programTitle
  const handleClick = !programTitle ? undefined : onClick

  // dont include tableGap  between cards in cardWidth
  const cardWidth = programWidthInPixels - tableGap

  return (
    <ProgramCardSC
      background={background}
      width={cardWidth}
      selected={selected}
      disabled={isDisabled}
      colorDisabled={colorDisabled}
      backgroundHovered={backgroundHovered}
      onClick={handleClick}
      height={height}
      ref={hoverRef}
      isLive={isLive}
      title={title}
      clickableCard={clickableCard}
      renderTitle={isRenderTitle}
      locked={locked}
      corners={corners}
      isThreeDots={isThreeDots}
    >
      {isRenderTitle && (
        <TextBlockProgramCard
          title={title}
          description={description}
          width={100}
          locked={locked}
        />
      )}
    </ProgramCardSC>
  )
}
